<template>
  <page-wrapper :title="$t('page.manageFiling.title')" :subtitle="subtitle" fluid>
    <div class="mb-4 d-md-none">
      <filing-sidebar @removeFiling="removeFiling" @submitFiling="submitFiling" @makeCorrections="makeCorrections"/>
    </div>
    <div class="d-flex">
      <div style="flex: 1 1 auto">
        <c-loading-area :loading="!ready" :height="500" :card="false">
          <template #default>
            <transition name="scroll-x-reverse-transition" mode="out-in" appear>
              <div v-if="showSuccess" key="success">
                <filing-success-splash-card @returnToSummary="returnToSummary"/>
              </div>
              <div v-else key="content">
                <filing-payment-locked-alert @unlockFiling="unlockFiling"/>
                <filing-instructions/>
                <filing-invalid-type-error @removeFiling="removeFiling"/>
                <filing-feedback/>
                <template v-if="isFullFiling">
                  <filing-content @submitFiling="submitFiling"/>
                </template>
                <template v-else-if="isAbbreviatedFiling">
                  <abbreviated-filing-content @submitFiling="submitFiling"/>
                </template>
                <filing-receipt-preview-popup v-model="filingReceiptPreviewPopupVisible"
                                              :receiptPreviewDTO="receiptPreviewDTO"
                                              @checkout="submitWithFees"/>
              </div>
            </transition>
          </template>
        </c-loading-area>
      </div>
      <div class="d-none d-md-block pl-4" style="flex: 0 0 280px">
        <filing-sidebar @removeFiling="removeFiling" @submitFiling="submitFiling" @makeCorrections="makeCorrections"/>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import _ from 'lodash';
import PageWrapper from '@components/support/PageWrapper';
import PageMixin from '@/mixins/page';
import FilingSidebar from '@components/support/manageFiling/FilingSidebar';
import FilingInstructions from '@components/support/manageFiling/FilingInstructions';
import FilingPaymentLockedAlert from '@components/support/manageFiling/FilingPaymentLockedAlert';
import FilingInvalidTypeError from '@components/support/manageFiling/FilingInvalidTypeError';
import FilingContent from '@components/support/manageFiling/FilingContent';
import FilingFeedback from '@components/support/manageFiling/FilingFeedback';
import FilingSuccessSplashCard from '@components/support/manageFiling/FilingSuccessSplashCard';
import AbbreviatedFilingContent from '@components/support/manageFiling/abbreviated/AbbreviatedFilingContent';
import FilingReceiptPreviewPopup from '@components/support/manageFiling/FilingReceiptPreviewPopup';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';

const filingStore = useFilingStore();

export default {
  components: { AbbreviatedFilingContent, PageWrapper, FilingSidebar, FilingInstructions, FilingContent, FilingFeedback,
    FilingSuccessSplashCard, FilingReceiptPreviewPopup, FilingPaymentLockedAlert, FilingInvalidTypeError },
  mixins: [ PageMixin ],
  props: {
    filingID: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      showSuccess: false,
      filingReceiptPreviewPopupVisible: false,
      receiptPreviewDTO: null
    };
  },
  computed: {
    ready () {
      return filingStore.ready;
    },
    filing () {
      return filingStore.filing;
    },
    subtitle () {
      return this.filing !== null ? this.filing.referenceName : null;
    },
    allStepValidationErrors () {
      return filingStore.getAllStepValidationErrors;
    },
    isFullFiling () {
      return this.filing.filingCodeExternalIdentifier !== this.CONST.FILING_CODE_EXTERNAL_IDENTIFIER_ABBREVIATED;
    },
    isAbbreviatedFiling () {
      return this.filing.filingCodeExternalIdentifier === this.CONST.FILING_CODE_EXTERNAL_IDENTIFIER_ABBREVIATED;
    },
    isAbbreviatedFilingReadyForSubmission () {
      return Filing.isAbbreviatedFilingReadyForSubmission(this.filing);
    }
  },
  created () {
    this.showSuccess = filingStore.showSuccessSplash;
    filingStore.loadAll(this.filingID);
  },
  methods: {
    removeFiling () {
      this.$cove.confirm({ message: this.$t('page.manageFiling.sidebar.removeFiling.warning') })
        .then((confirm) => {
          if (confirm)
          {
            this.$cove.blockNow();
            filingStore.remove()
              .then(() => {
                this.$cove.notify({ message: this.$t('application.remove.success'), color: 'success' });
                this.$router.push({ name: 'work-filings' });
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    },
    makeCorrections () {
      this.$cove.confirm({ message: this.$t('page.manageFiling.sidebar.makeCorrections.confirm') })
        .then((confirm) => {
          if (confirm)
          {
            this.$cove.blockNow();
            filingStore.clone()
              .then((newFilingID) => {
                this.$cove.notify({ message: this.$t('page.manageFiling.sidebar.makeCorrections.success'), color: 'success' });
                this.$router.push({ name: 'manage-filing', params: { filingID: newFilingID } });
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    },
    // main entry point to submit the filing. will delegate from here depending on filing fee situation.
    submitFiling () {
      if ( (this.isFullFiling && !_.isEmpty(this.allStepValidationErrors))
          || (this.isAbbreviatedFiling && !this.isAbbreviatedFilingReadyForSubmission) )
      {
        this.$cove.notify({ message: this.$t('page.manageFiling.submit.incompleteSteps'), color: 'error' });
      }
      else
      {
        this.$cove.validation.clear();
        this.$cove.block();
        Filing.getFilingFeesReceiptPreview(this.filingID)
          .then((receiptPreviewDTO) => {
            // if they owe money prompt them with a preview
            let amount = receiptPreviewDTO.amount;
            if (typeof amount === 'number' && amount > 0)
            {
              this.previewFilingFees(receiptPreviewDTO);
            }
            // else just give them a standard confirmation
            else if (typeof amount === 'number' && amount === 0)
            {
              this.submitWithoutFees();
            }
            else
            {
              this.$cove.notify({ message: this.$t('page.manageFiling.error.filingFeePreview'), color: 'error' });
              throw new Error('Error - filing fee preview amount was not a valid number');
            }
          })
          .finally(() => {
            this.$cove.unblock();
          });
      }
    },
    previewFilingFees (receiptPreviewDTO) {
      this.receiptPreviewDTO = receiptPreviewDTO;
      this.filingReceiptPreviewPopupVisible = true;
    },
    submitWithFees () {
      this.$cove.blockNow();
      filingStore.submit(this.receiptPreviewDTO.amount)
        .then((response) => {
          // if we need to redirect to payment provider, do it now (and keep the blocker on)
          let redirect = _.get(response, 'data.redirectLocation', null);
          if (!_.isNil(redirect))
          {
            window.location.href = response.data.redirectLocation;
          }
          // else, we were able to capture the funds already so show the success
          else
          {
            this.$cove.notify({ message: this.$t('page.manageFiling.submit.success'), color: 'success' });
            this.$router.push({ name: 'filing-submission-success', params: { filingID: this.filingID } })
              // catch here to eat the exception about the cancelled navigation that occurs on success router hook (expected)
              .catch(() => {});
            this.$cove.unblock();
          }
        })
        .catch(() => {
          this.$cove.unblock();
        });
    },
    submitWithoutFees () {
      this.$cove.confirm({ message: this.$t('page.manageFiling.submit.confirm') })
        .then((confirm) => {
          if (confirm)
          {
            this.$cove.blockNow();
            filingStore.submit()
              .then(() => {
                this.$cove.notify({ message: this.$t('page.manageFiling.submit.success'), color: 'success' });
                this.$router.push({ name: 'filing-submission-success', params: { filingID: this.filingID } })
                  // catch here to eat the exception about the cancelled navigation that occurs on success router hook (expected)
                  .catch(() => {});
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    },
    returnToSummary () {
      this.showSuccess = false;
    },
    unlockFiling () {
      this.$cove.blockNow();
      filingStore.unlock()
        .then(() => {
          this.$cove.notify({ message: this.$t('page.manageFiling.unlockFiling.success'), color: 'success' });
          return filingStore.loadAll(this.filingID);
        })
        .finally(() => {
          this.$cove.unblock();
        });
    }
  }
};
</script>